"use client";

/**
 * TestimonialCard.tsx
 *
 * A single testimonial card used inside the Testimonials slider.
 * Renders the star rating, review text, avatar and author info.
 *
 * Note: avatar images are validated before rendering — if the URL
 * is invalid we fall back to an initial-letter placeholder.
 */

import React from "react";
import { Star } from "lucide-react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface Testimonial {
  text: string;
  authorName: string;
  authorRole?: string;
  rating?: number;
  avatar?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Only render <img> for URLs we trust (http or data URI) */
function isValidUrl(url: unknown): url is string {
  return (
    typeof url === "string" &&
    (url.startsWith("http") || url.startsWith("data:"))
  );
}

/** Clamp the star rating to 1–5 */
function safeRating(raw: unknown): number {
  const n = Number(raw);
  if (!isFinite(n) || isNaN(n)) return 5;
  return Math.min(5, Math.max(1, Math.round(n)));
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface TestimonialCardProps {
  testimonial: Testimonial;
}

const TestimonialCard = ({ testimonial }: TestimonialCardProps) => {
  const stars = safeRating(testimonial.rating);
  const initial = testimonial.authorName?.charAt(0) || "G";

  return (
    <div className="relative bg-white p-10 md:p-8 lg:p-10 shadow-2xl rounded-[2px] h-full flex flex-col">
      {/* Inner gold border — gives the double-frame look */}
      <div className="absolute inset-[14px] border border-[#c59d5f]/50 rounded-[2px] pointer-events-none" />

      <div className="relative z-10 flex flex-col h-full items-center text-center">
        {/* Star rating */}
        <div className="flex justify-center gap-1 mb-4">
          {Array.from({ length: stars }).map((_, i) => (
            <Star key={i} size={14} className="text-[gold] fill-[gold]" />
          ))}
        </div>

        {/* Review text */}
        <div className="flex-1 mb-2">
          <p className="font-playfair italic text-[#555] text-lg md:text-[19px] leading-relaxed">
            &ldquo;{testimonial.text}&rdquo;
          </p>
        </div>

        {/* Author */}
        <div className="flex flex-col items-center mt-auto">
          <div className="relative mb-6 w-16 h-16 rounded-full border-2 border-[#c59d5f] p-0.5 bg-white">
            {isValidUrl(testimonial.avatar) ? (
              <img
                src={testimonial.avatar}
                className="w-full h-full object-cover rounded-full"
                alt={testimonial.authorName || "Guest"}
                onError={(e) => {
                  // If the image fails to load, hide it (the placeholder below shows instead)
                  (e.currentTarget as HTMLImageElement).style.display = "none";
                }}
              />
            ) : (
              <div className="w-full h-full rounded-full bg-[#c59d5f]/20 flex items-center justify-center text-[#c59d5f] font-bold text-xl">
                {initial}
              </div>
            )}

            {/* Decorative gold ribbon tag below the avatar */}
            <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 bg-[#c59d5f] w-3.5 h-5 clip-path-ribbon" />
          </div>

          <h5 className="font-inter text-gray-900 font-bold text-sm mb-1">
            {testimonial.authorName || "Guest"}
          </h5>
          <span className="font-sacramento text-[#c59d5f] text-2xl">
            {testimonial.authorRole || ""}
          </span>
        </div>
      </div>
    </div>
  );
};

export default TestimonialCard;
